import type {
	EventLogV2Payload,
	EventsLogResponse,
	EventsLogResult,
	EventsLogResultData,
} from 'src/types/requests/eventslog/events_log';

import {
	isEventLogV2Payload,
	isEventLogV2Enabled,
	parseEventLogPayload,
} from './event_log_version_helpers';
import { asDoorEventState, coerceEventLogV2Payload } from './event_log_v2_payload_adapter';
import {
	isFlatKeyedEventLogPayload,
	normalizeFlatEventLogPayload,
} from './event_log_v2_flat_normalizer';

type V2Normalizer = (tag: string, payload: EventLogV2Payload) => unknown[] | null;
type PayloadRecord = Record<string, unknown>;

const COVER_CRITICAL_ERRORS = ['obstruction', 'safety_switch', 'bad_feedback', 'cal_abort'];
const SWITCH_ERROR_KEYS = ['overpower', 'overtemp', 'overvoltage', 'undervoltage'];

const getChannelFromComponent = (component?: string): number => {
	if (!component) return 0;
	const id = Number(component.split(':')[1]);
	return Number.isNaN(id) ? 0 : id;
};

/** Prefer top-level `channel` (flat/mixed payloads), else parse from `component`. */
const resolveChannel = (payload: EventLogV2Payload): number => {
	if (typeof payload.channel === 'number' && !Number.isNaN(payload.channel)) {
		return payload.channel;
	}
	return getChannelFromComponent(payload.component);
};

const payloadSource = (payload: EventLogV2Payload, preferDetectedFrom = false): PayloadRecord => {
	if (preferDetectedFrom && payload.detected_from) {
		return payload.detected_from;
	}
	return (payload.current || payload.previous || payload.detected_from || {}) as PayloadRecord;
};

const pickField = (source: PayloadRecord, ...keys: string[]): unknown => {
	for (const key of keys) {
		const value = source[key];
		if (value !== undefined && value !== null) {
			return value;
		}
	}
	return undefined;
};

const pickFromSnapshots = (
	current: PayloadRecord | undefined,
	previous: PayloadRecord | undefined,
	...keys: string[]
): unknown => {
	for (const key of keys) {
		const currentValue = current?.[key];
		if (currentValue !== undefined && currentValue !== null && currentValue !== previous?.[key]) {
			return currentValue;
		}
	}
	const source = { ...previous, ...current };
	return pickField(source, ...keys);
};

const tagRow = (tag: string, ...fields: unknown[]): unknown[] => [tag, ...fields];

const normalizeScenePayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const tstamp =
		pickFromSnapshots(payload.current, payload.previous, 'tstamp', 'timestamp') ??
		pickField(payload as PayloadRecord, 'tstamp', 'timestamp');
	const rootname =
		pickFromSnapshots(payload.current, payload.previous, 'rootname', 'name') ??
		pickField(payload as PayloadRecord, 'rootname', 'name', 'sceneId');
	return tagRow(tag, tstamp, rootname).filter((value) => value !== undefined);
};

const normalizeOnOffPayload = (tag: string, payload: EventLogV2Payload): unknown[] =>
	tagRow(
		tag,
		resolveChannel(payload),
		pickFromSnapshots(payload.current, payload.previous, 'output', 'on', 'turn') ??
			pickField(payload as PayloadRecord, 'output', 'on', 'turn'),
		pickFromSnapshots(payload.current, payload.previous, 'source', 'reason', 'src') ??
			pickField(payload as PayloadRecord, 'source', 'reason', 'src'),
	);

const normalizeCoverPayload = (tag: string, payload: EventLogV2Payload): unknown[] =>
	tagRow(
		tag,
		resolveChannel(payload),
		pickFromSnapshots(payload.current, payload.previous, 'state') ??
			pickField(payload as PayloadRecord, 'state'),
		pickFromSnapshots(payload.current, payload.previous, 'current_pos', 'apower') ??
			pickField(payload as PayloadRecord, 'current_pos', 'apower', 'position'),
	);

const normalizeBrightnessPayload = (tag: string, payload: EventLogV2Payload): unknown[] =>
	tagRow(
		tag,
		resolveChannel(payload),
		pickFromSnapshots(payload.current, payload.previous, 'brightness') ??
			pickField(payload as PayloadRecord, 'brightness'),
	);

const normalizeWakeUpPayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const source = payloadSource(payload, true);
	return tagRow(
		tag,
		pickField(source, 'reason', 'src'),
		pickField(source, 'temperature', 'tmp', 'tC'),
		pickField(source, 'battery', 'bat'),
		pickField(source, 'humidity', 'alarm_hum', 'rh'),
	);
};

const normalizeDoorWindowPayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const source = payloadSource(payload);
	return tagRow(
		tag,
		pickField(source, 'reason'),
		asDoorEventState(pickField(source, 'state', 'door', 'open')),
		pickField(source, 'battery', 'bat'),
		pickField(source, 'lux', 'lux_value'),
		pickField(source, 'illumination', 'lux_illumination'),
		pickField(source, 'tilt'),
		pickField(source, 'vibration'),
		pickField(source, 'temperature', 'tmp'),
	);
};

const normalizeMotionPayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const source = payloadSource(payload);
	return tagRow(
		tag,
		pickFromSnapshots(source, payload.previous as PayloadRecord, 'motion', 'is_active') ??
			pickField(payload as PayloadRecord, 'motion', 'is_active'),
		pickField(source, 'battery', 'bat') ?? pickField(payload as PayloadRecord, 'battery', 'bat'),
		pickField(source, 'lux', 'lux_value', 'illuminance') ??
			pickField(payload as PayloadRecord, 'lux', 'lux_value', 'illuminance'),
		pickField(source, 'illumination', 'lux_illumination') ??
			pickField(payload as PayloadRecord, 'illumination', 'lux_illumination'),
		pickField(source, 'temperature', 'tmp', 'tC') ??
			pickField(payload as PayloadRecord, 'temperature', 'tmp', 'tC'),
	);
};

const normalizeCriticalErrorPayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const channel = resolveChannel(payload);
	const source = payloadSource(payload);

	for (const errorKey of [...SWITCH_ERROR_KEYS, ...COVER_CRITICAL_ERRORS]) {
		const errorValue = source[errorKey];
		if (errorValue !== undefined && errorValue !== null && errorValue !== false) {
			return typeof errorValue === 'boolean'
				? tagRow(tag, channel, errorKey)
				: tagRow(tag, channel, errorKey, errorValue);
		}
	}

	const activeErrors = source.errors;
	if (Array.isArray(activeErrors) && activeErrors.length > 0) {
		const errorKey = String(activeErrors[0]);
		const errorValue = source[errorKey];
		return errorValue !== undefined
			? tagRow(tag, channel, errorKey, errorValue)
			: tagRow(tag, channel, errorKey);
	}

	return tagRow(tag, channel);
};

const normalizeInputStatePayload = (tag: string, payload: EventLogV2Payload): unknown[] =>
	tagRow(
		tag,
		resolveChannel(payload),
		pickFromSnapshots(payload.current, payload.previous, 'state', 'event', 'reason') ??
			pickField(payload as PayloadRecord, 'state', 'event', 'reason'),
		payload.component ||
			pickField(
				(payload.current || payload.previous || {}) as PayloadRecord,
				'component',
				'ref',
			) ||
			pickField(payload as PayloadRecord, 'component', 'ref'),
	);

const normalizeModulePayload = (tag: string, payload: EventLogV2Payload): unknown[] =>
	tagRow(
		tag,
		payload.component || '',
		pickFromSnapshots(payload.current, payload.previous, 'value', 'output', 'state', 'event'),
	);

const normalizeBleDoorPayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const source = payloadSource(payload);
	return tagRow(
		tag,
		pickField(source, 'reason'),
		asDoorEventState(pickField(source, 'state', 'door', 'open')),
		pickField(source, 'battery', 'bat'),
		pickField(source, 'lux', 'lux_value', 'lux'),
		pickField(source, 'tilt', 'rotation'),
	);
};

const normalizeHntPayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const source = payloadSource(payload);
	return tagRow(
		tag,
		pickField(source, 'temperature', 'tmp', 'tC'),
		pickField(source, 'humidity', 'alarm_hum', 'rh'),
		pickField(source, 'battery', 'bat'),
		pickField(source, 'illumination', 'lux_illumination', 'lux'),
	);
};

const normalizeVibrationDistancePayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const source = payloadSource(payload);
	return tagRow(
		tag,
		pickField(source, 'state', 'vibration'),
		pickField(source, 'distance'),
		pickField(source, 'battery', 'bat'),
	);
};

const normalizePresencePayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const source = payloadSource(payload, true);
	const ref = pickField(source, 'ref', 'presence_zone', 'zone') ?? payload.component ?? undefined;
	const numObjects = pickField(source, 'num_objects');

	let state = pickField(source, 'state', 'presence_detected', 'presence');
	if (state === undefined) {
		const track = pickField(source, 'track');
		const noTrack = pickField(source, 'no_track');
		if (track !== undefined) {
			state = track;
		} else if (noTrack !== undefined) {
			state = !noTrack;
		}
	}

	const hasNewShape = ref !== undefined || numObjects !== undefined || state !== undefined;
	if (hasNewShape) {
		return tagRow(tag, ref, state, numObjects);
	}

	return tagRow(
		tag,
		undefined,
		undefined,
		undefined,
		pickField(source, 'track'),
		pickField(source, 'no_track'),
		pickField(source, 'presence_detected', 'presence'),
		pickField(source, 'presence_zone', 'zone'),
		pickField(source, 'illumination'),
	);
};

const normalizeCuryPayload = (tag: string, payload: EventLogV2Payload): unknown[] =>
	tagRow(
		tag,
		resolveChannel(payload),
		pickFromSnapshots(payload.current, payload.previous, 'output', 'on') ??
			pickField(payload as PayloadRecord, 'output', 'on', 'turn'),
		pickFromSnapshots(payload.current, payload.previous, 'vial_level', 'level') ??
			pickField(payload as PayloadRecord, 'vial_level', 'level'),
	);

const normalizeCameraPayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const source = payloadSource(payload);
	const topLevel = payload as PayloadRecord;
	return tagRow(
		tag,
		pickField(source, 'camerazone', 'zone') ?? pickField(topLevel, 'camerazone', 'zone'),
		pickFromSnapshots(source, payload.previous as PayloadRecord, 'motion', 'is_active') ??
			pickField(topLevel, 'motion', 'is_active'),
		pickField(source, 'recordingId', 'recId', 'rec_id') ??
			pickField(topLevel, 'recordingId', 'recId', 'rec_id'),
		pickField(source, 'imageId', 'mediaId', 'media_id') ??
			pickField(topLevel, 'imageId', 'mediaId', 'media_id'),
	);
};

const normalizeCameraArmPayload = (tag: string, payload: EventLogV2Payload): unknown[] => {
	const source = payloadSource(payload);
	const topLevel = payload as PayloadRecord;
	return tagRow(
		tag,
		pickFromSnapshots(source, payload.previous as PayloadRecord, 'arm') ??
			pickField(topLevel, 'arm'),
		pickFromSnapshots(source, payload.previous as PayloadRecord, 'privacy') ??
			pickField(topLevel, 'privacy'),
	);
};

const normalizeBreakerPayload = (tag: string, payload: EventLogV2Payload): unknown[] =>
	tagRow(
		tag,
		pickFromSnapshots(payload.current, payload.previous, 'output', 'on') ??
			pickField(payload as PayloadRecord, 'output', 'on', 'turn', 'state'),
		pickFromSnapshots(payload.current, payload.previous, 'source', 'reason', 'src') ??
			pickField(payload as PayloadRecord, 'source', 'reason', 'src'),
	);

const normalizeLockPayload = (tag: string, payload: EventLogV2Payload): unknown[] =>
	tagRow(tag, pickFromSnapshots(payload.current, payload.previous, 'bolt_state', 'lock', 'state'));

const normalizeTemperaturePayload = (tag: string, payload: EventLogV2Payload): unknown[] =>
	tagRow(tag, pickFromSnapshots(payload.current, payload.previous, 'temperature', 'tmp', 'tC'));

const EVENT_V2_NORMALIZERS: Record<number, V2Normalizer> = {
	1: normalizeOnOffPayload,
	2: (tag, payload) => tagRow(tag, resolveChannel(payload)),
	3: normalizeTemperaturePayload,
	4: normalizeWakeUpPayload,
	5: normalizeCoverPayload,
	6: normalizeScenePayload,
	7: normalizeDoorWindowPayload,
	8: normalizeBrightnessPayload,
	9: (tag, payload) =>
		tagRow(
			tag,
			pickFromSnapshots(payload.current, payload.previous, 'alarm_gas', 'gas') ??
				pickField(payload as PayloadRecord, 'alarm_gas', 'gas'),
		),
	10: normalizeMotionPayload,
	11: normalizeCriticalErrorPayload,
	12: (tag, payload) =>
		tagRow(
			tag,
			resolveChannel(payload),
			pickFromSnapshots(payload.current, payload.previous, 'smoke', 'alarm') ??
				pickField(payload as PayloadRecord, 'smoke', 'alarm'),
		),
	13: normalizeInputStatePayload,
	14: normalizeBleDoorPayload,
	15: normalizeMotionPayload,
	16: normalizeHntPayload,
	17: normalizeModulePayload,
	18: normalizeLockPayload,
	19: normalizeVibrationDistancePayload,
	20: normalizePresencePayload,
	21: normalizeCuryPayload,
	22: normalizeCameraPayload,
	23: normalizeBreakerPayload,
	24: normalizeCameraArmPayload,
};

const normalizeV2Entry = (entry: EventsLogResultData): EventsLogResultData => {
	const parsed = parseEventLogPayload(entry.p);
	if (!isEventLogV2Payload(parsed)) {
		return entry;
	}

	if (isFlatKeyedEventLogPayload(parsed)) {
		const flatArray = normalizeFlatEventLogPayload(entry.e, parsed, entry.tag);
		if (flatArray) {
			return { ...entry, p: JSON.stringify(flatArray) };
		}
		console.warn(`[event_log_v2] No flat normalizer for event ID ${entry.e}`, entry);
		return entry;
	}

	const normalizer = EVENT_V2_NORMALIZERS[entry.e];
	if (!normalizer) {
		console.warn(`[event_log_v2] No normalizer for event ID ${entry.e}`, entry);
		return entry;
	}

	const payload = coerceEventLogV2Payload(parsed);
	const v1Array = normalizer(entry.tag, payload);
	if (!v1Array) {
		return entry;
	}

	return { ...entry, p: JSON.stringify(v1Array) };
};

/** Converts v2 JSON payloads in a response to v1 positional arrays for the legacy parser. */
export const normalizeEventsLogResponse = (data: EventsLogResponse): EventsLogResponse => {
	if (!data?.result) {
		return data;
	}

	const result: EventsLogResult = {};
	for (const key in data.result) {
		result[key] = data.result[key].map(normalizeV2Entry);
	}

	return { ...data, result };
};

export const normalizeEventLogEntry = normalizeV2Entry;

/** Normalizes v2 payloads to v1 arrays when the allowlisted account uses event log v2. */
export const prepareEventsLogResponse = (data: EventsLogResponse): EventsLogResponse =>
	isEventLogV2Enabled() ? normalizeEventsLogResponse(data) : data;
